import React from "react";
import {
  ShieldAlert,
  Plug,
  LayoutGrid,
  Radio,
  Tv,
  Cast,
  Users,
  Trophy,
  Flag,
  Check,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card, Badge } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { PageHero, FeatureCard, CTASection, WhatsAppButton } from "../../components/marketing/Shared";
import { SCOREBOARD } from "../../data/mock";

function EventWallMockup() {
  return (
    <div className="rounded-2xl border border-white/10 bg-neutral-950 p-3 shadow-2xl">
      <div className="mb-2.5 flex items-center justify-between px-1">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ctf">
          <Trophy className="h-3.5 w-3.5" /> Event Wall · LIVE
        </span>
        <span className="rounded bg-ctf/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-ctf">TOP 10</span>
      </div>
      <div className="grid grid-cols-[1.4fr_1fr] gap-2">
        <div className="space-y-1">
          {SCOREBOARD.slice(0, 6).map((r) => (
            <div key={r.rank} className="flex items-center gap-2 rounded-md bg-white/[0.04] px-2 py-1.5">
              <span className="w-4 text-center font-mono text-[10px] font-bold text-ctf">{r.rank}</span>
              <span className="flex-1 truncate font-mono text-[11px] text-white/80">{r.handle}</span>
              <span className="font-mono text-[11px] font-bold text-white">{r.points}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="relative overflow-hidden rounded-md border border-white/10 bg-neutral-900">
              <div
                className="aspect-square w-full"
                style={{ background: "repeating-linear-gradient(135deg,hsl(var(--ctf)/.08) 0 6px,hsl(var(--ctf)/.14) 6px 12px)" }}
              />
              {i === 1 && (
                <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-ctf/90 px-1 py-0.5 text-[7px] font-bold text-black">
                  <Flag className="h-2 w-2" /> FLAG!
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between rounded-lg bg-black/50 px-3 py-1.5">
        <span className="font-mono text-[10px] text-ctf">● OBS · broadcast</span>
        <span className="font-mono text-[10px] text-white/40">official time 03:14:22</span>
      </div>
    </div>
  );
}

export default function CTF() {
  const { t } = useI18n();
  const features = [
    { icon: ShieldAlert, k: "anticheat" },
    { icon: Plug, k: "integration" },
    { icon: LayoutGrid, k: "eventwall" },
    { icon: Radio, k: "stream" },
    { icon: Tv, k: "overlays" },
    { icon: Cast, k: "cast" },
    { icon: Users, k: "scale" },
  ];

  return (
    <>
      <PageHero
        breadcrumb={t("nav.ctf")}
        accent="ctf"
        eyebrow={t("ctf.hero.eyebrow")}
        title={t("ctf.hero.title")}
        lead={t("ctf.hero.lead")}
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contato" size="lg" className="bg-ctf text-black hover:brightness-110">
            {t("common.bookDemo")}
          </Button>
          <WhatsAppButton variant="outline" className="border-white/25 text-white hover:bg-white/10" />
        </div>
      </PageHero>

      {/* Event Wall showcase */}
      <Section>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Badge tone="ctf">{t("ctf.f.eventwall.title")}</Badge>
              <h2 className="mt-4 text-3xl">{t("ctf.f.eventwall.title")}</h2>
              <p className="mt-4 text-muted-foreground">{t("ctf.f.eventwall.desc")}</p>
              <ul className="mt-6 space-y-3">
                {["stream", "overlays", "cast"].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-ctf" />
                    <span className="text-sm">
                      <strong className="font-semibold">{t(`ctf.f.${k}.title`)}</strong> — {t(`ctf.f.${k}.desc`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <EventWallMockup />
          </div>
        </Container>
      </Section>

      {/* Features grid */}
      <Section alt>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <FeatureCard key={f.k} icon={f.icon} accent="ctf" title={t(`ctf.f.${f.k}.title`)} desc={t(`ctf.f.${f.k}.desc`)} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Two audiences */}
      <Section>
        <Container>
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ctf/12 text-ctf">
                <SlidersIcon />
              </span>
              <h3 className="mt-5 text-xl">{t("ctf.audience.organizers")}</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {[t("ctf.f.anticheat.desc"), t("ctf.f.integration.desc"), t("ctf.f.cast.desc")].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ctf" /> {x}
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ctf/12 text-ctf">
                <Tv className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-xl">{t("ctf.audience.public")}</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {[t("ctf.f.stream.desc"), t("ctf.f.overlays.desc"), t("ctf.f.eventwall.desc")].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ctf" /> {x}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}

function SlidersIcon() {
  return <ShieldAlert className="h-6 w-6" />;
}
